import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { trackCompleteRegistration } from "../lib/metaPixel";
import { pushSignUp, pushLogin } from "../lib/gtmDataLayer";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=not loaded, false=unauth, object=user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: skip /me if returning from OAuth
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("sdf_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("sdf_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // iter46: backend may return a 2-step result asking for a 2FA code.
    // In that case we DON'T persist a session — caller (Login.jsx) handles
    // the second step via verifyTwoFactor() below.
    if (data?.["2fa_required"]) {
      return { twoFactorRequired: true, ephemeralToken: data.ephemeral_token };
    }
    localStorage.setItem("sdf_token", data.access_token);
    setUser(data.user);
    // iter57 — GA4 login event
    try { pushLogin("email"); } catch (_) { /* never break login */ }
    return data.user;
  };

  const verifyTwoFactor = async (ephemeralToken, otp) => {
    const { data } = await api.post("/auth/login/verify-2fa", {
      ephemeral_token: ephemeralToken, otp,
    });
    localStorage.setItem("sdf_token", data.access_token);
    setUser(data.user);
    try { pushLogin("email_2fa"); } catch (_) { /* never break login */ }
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("sdf_token", data.access_token);
    setUser(data.user);
    // iter52 — Meta CompleteRegistration with the new user's PII so CAPI
    // can match them on subsequent visits.
    try {
      trackCompleteRegistration("email");
      pushSignUp("email");
    } catch (_) { /* analytics must never break signup */ }
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* server-side logout best-effort */ }
    localStorage.removeItem("sdf_token");
    setUser(false);
  };

  const setUserDirect = (u) => setUser(u);

  /**
   * Persist a freshly-issued token + user and mark the session active.
   * Used by flows that return their own session (e.g. password reset)
   * so the user is auto-signed-in without a second /auth/login round-trip.
   */
  const applyTokenSession = (token, userObj) => {
    if (token) localStorage.setItem("sdf_token", token);
    if (userObj) setUser(userObj);
  };

  // Memoize the provider value so consumers (every route) don't re-render
  // when AuthProvider's parent re-renders without state changes.
  const value = useMemo(
    () => ({ user, loading, login, verifyTwoFactor, register, logout, refresh: checkAuth, setUser: setUserDirect, setUserDirect, applyTokenSession }),
    [user, loading, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
